// src/feature-module/support/SupportGrants.tsx
//
// What support has GRANTED, and the ability to take it back.
//
// This screen was missing entirely. Support could start a read-only session
// into a tenant and waive that tenant's KYC gates — both time-boxed, both
// revocable in the backend — and then had no way to see either afterwards, let
// alone revoke one. `useListImpersonationsQuery`, `useRevokeImpersonationMutation`,
// `useListKycExemptionsQuery` and `useRevokeKycExemptionMutation` all existed and
// were called by nothing.
//
// The gap mattered most where it was least visible: the tenant health panel
// shows a "Live support sessions" count, so a reviewer could see that something
// was live and had nowhere to click. A grant you cannot review is a grant you
// cannot govern.
//
// Live grants sort first because they are the only ones anyone can act on.

import React from "react";
import { Card, Table, Tag, Button, Popconfirm, Tabs, Empty, Typography, Space, message } from "antd";
import { Link } from "react-router-dom";
import {
  useListImpersonationsQuery,
  useRevokeImpersonationMutation,
  useListKycExemptionsQuery,
  useRevokeKycExemptionMutation,
} from "../../core/redux/services/adminSupportApi";
import { getApiErrorMessageWithRef } from "../../core/utils/apiError";

const { Text } = Typography;

const when = (v?: string | null) => (v ? new Date(v).toLocaleString() : "—");

/** Minutes remaining, or null once it has lapsed. */
function minutesLeft(expiresAt?: string | null): number | null {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  return ms > 0 ? Math.ceil(ms / 60000) : null;
}

const ImpersonationTable: React.FC = () => {
  const { data, isFetching, isError, refetch } = useListImpersonationsQuery(undefined as any);
  const [revoke, { isLoading: revoking }] = useRevokeImpersonationMutation();

  const rows: any[] = (data as any)?.data ?? (Array.isArray(data) ? data : []);
  // Live first — they are the only ones anyone can act on.
  const sorted = [...rows].sort(
    (a, b) => Number(!!b.isActive) - Number(!!a.isActive) || +new Date(b.createdAt) - +new Date(a.createdAt),
  );

  const onRevoke = async (id: string) => {
    try {
      await revoke(id).unwrap();
      message.success("Session revoked. The token stops working on its next request.");
      refetch();
    } catch (e) {
      message.error(getApiErrorMessageWithRef(e, "Could not revoke the session."));
    }
  };

  if (isError) return <Empty description="Couldn't load support sessions" />;

  return (
    <Table
      rowKey="id"
      size="small"
      loading={isFetching}
      dataSource={sorted}
      locale={{ emptyText: "No support sessions have been granted." }}
      pagination={{ pageSize: 10, showSizeChanger: true }}
      scroll={{ x: "max-content" }}
      columns={[
        {
          title: "State",
          width: 110,
          render: (_: unknown, r: any) => {
            if (r.revokedAt) return <Tag>Revoked</Tag>;
            const left = minutesLeft(r.expiresAt);
            return left === null ? <Tag>Expired</Tag> : <Tag color="orange">Live · {left}m</Tag>;
          },
        },
        {
          title: "Acting as",
          render: (_: unknown, r: any) => (
            <>
              {/* Names, not cuids — the list used to return only ids. */}
              <div>{r.targetName || r.targetEmail || <Text type="secondary">unknown user</Text>}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {r.targetEmail && r.targetName ? `${r.targetEmail} · ` : ""}
                {r.targetTenantName ? (
                  <Link to={`/tenants/${r.targetTenantId}/360`}>{r.targetTenantName}</Link>
                ) : (
                  r.targetTenantId
                )}
              </Text>
            </>
          ),
        },
        { title: "Granted to", dataIndex: "impersonatorEmail", render: (v: string) => v || "—" },
        { title: "Reason", dataIndex: "reason", ellipsis: true },
        { title: "Started", dataIndex: "createdAt", render: when, width: 180 },
        { title: "Expires", dataIndex: "expiresAt", render: when, width: 180 },
        {
          title: "",
          width: 100,
          render: (_: unknown, r: any) =>
            r.isActive ? (
              <Popconfirm
                title="Revoke this session?"
                description="The support engineer loses access on their next request."
                onConfirm={() => onRevoke(r.id)}
              >
                <Button size="small" danger loading={revoking}>
                  Revoke
                </Button>
              </Popconfirm>
            ) : null,
        },
      ]}
    />
  );
};

const ExemptionTable: React.FC = () => {
  const { data, isFetching, isError, refetch } = useListKycExemptionsQuery(undefined as any);
  const [revoke, { isLoading: revoking }] = useRevokeKycExemptionMutation();

  const rows: any[] = (data as any)?.data ?? (Array.isArray(data) ? data : []);
  const live = (r: any) => !r.revokedAt && new Date(r.expiresAt).getTime() > Date.now();
  const sorted = [...rows].sort(
    (a, b) => Number(live(b)) - Number(live(a)) || +new Date(b.createdAt) - +new Date(a.createdAt),
  );

  const onRevoke = async (id: string) => {
    try {
      await revoke(id).unwrap();
      message.success("Exemption revoked. The KYC gates apply again immediately.");
      refetch();
    } catch (e) {
      message.error(getApiErrorMessageWithRef(e, "Could not revoke the exemption."));
    }
  };

  if (isError) return <Empty description="Couldn't load KYC exemptions" />;

  return (
    <Table
      rowKey="id"
      size="small"
      loading={isFetching}
      dataSource={sorted}
      locale={{ emptyText: "No KYC exemptions have been granted." }}
      pagination={{ pageSize: 10, showSizeChanger: true }}
      scroll={{ x: "max-content" }}
      columns={[
        {
          title: "State",
          width: 110,
          render: (_: unknown, r: any) => {
            if (r.revokedAt) return <Tag>Revoked</Tag>;
            const left = minutesLeft(r.expiresAt);
            return left === null ? <Tag>Expired</Tag> : <Tag color="gold">Live · {Math.ceil(left / 60)}h</Tag>;
          },
        },
        {
          title: "Tenant",
          render: (_: unknown, r: any) =>
            r.tenantId ? <Link to={`/tenants/${r.tenantId}/360`}>{r.tenantName || r.tenantId}</Link> : "—",
        },
        { title: "Granted by", dataIndex: "grantedByEmail", render: (v: string) => v || "—" },
        { title: "Reason", dataIndex: "reason", ellipsis: true },
        { title: "Granted", dataIndex: "createdAt", render: when, width: 180 },
        { title: "Expires", dataIndex: "expiresAt", render: when, width: 180 },
        {
          title: "",
          width: 100,
          render: (_: unknown, r: any) =>
            live(r) ? (
              <Popconfirm
                title="Revoke this exemption?"
                description="The tenant's KYC gates start applying again straight away."
                onConfirm={() => onRevoke(r.id)}
              >
                <Button size="small" danger loading={revoking}>
                  Revoke
                </Button>
              </Popconfirm>
            ) : null,
        },
      ]}
    />
  );
};

const SupportGrants: React.FC = () => (
  <div className="page-wrapper">
    <div className="content">
      <div className="mb-3">
        <h4 className="mb-1">Support grants</h4>
        <p className="text-muted mb-0">
          Read-only sessions and KYC waivers that support has issued — and what is still live.
        </p>
      </div>

      <Card>
        <Tabs
          items={[
            { key: "sessions", label: "Read-only sessions", children: <ImpersonationTable /> },
            { key: "kyc", label: "KYC exemptions", children: <ExemptionTable /> },
          ]}
        />
      </Card>

      <Space className="mt-3">
        <Text type="secondary" style={{ fontSize: 12 }}>
          Both are time-boxed and expire on their own. Revoking ends one early.
        </Text>
      </Space>
    </div>
  </div>
);

export default SupportGrants;
